import React, { Dispatch, useEffect, useMemo, useState } from "react";
import { RootStateOrAny, useDispatch, useSelector } from "react-redux";
import { fetchGeoScopeData } from "../../../actions";
import "../../../assets/styles/component/dropDown.scss";
import { useDropdownVisible } from "../../../hooks";

export interface GeoScopeGroup {
  id: number;
  name: string;
}

interface GeographicalScopeInputProps {
  selectedGroupIds: number[];
  setSelectedGroupIds: Dispatch<number[]>;
}

export const GeographicalScopeInput = (Props: GeographicalScopeInputProps) => {
  const { selectedGroupIds, setSelectedGroupIds } = Props;
  const geoScopeData = useSelector((state: RootStateOrAny) => state.filters.geo_scope);
  const [groups, setGroups] = useState<GeoScopeGroup[]>([]);
  const [query, setQuery] = useState("");
  const { wrapperRef: geoRef, isVisible: showOptions, setIsVisible: setShowOptions } = useDropdownVisible(false);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchGeoScopeData());
  }, []);

  useEffect(() => {
    // GET /authentication/sign-up-geos now returns a flat, unfiltered `{id, name}[]` of every real
    // Group - no region nesting. The backend pre-orders it (most-used groups first, then
    // alphabetical), so this is kept in that order rather than re-sorted.
    if (geoScopeData?.length) {
      setGroups(geoScopeData);
    }
  }, [geoScopeData]);

  const selectedGroups = useMemo(
    () =>
      selectedGroupIds
        .map((id) => groups.find((group) => group.id === id))
        .filter((group): group is GeoScopeGroup => Boolean(group)),
    [selectedGroupIds, groups]
  );

  const filteredOptions = useMemo(
    () =>
      groups.filter((group) => !selectedGroupIds.includes(group.id) && group.name.toLowerCase().includes(query.trim().toLowerCase())),
    [groups, selectedGroupIds, query]
  );

  const handleSelect = (group: GeoScopeGroup) => {
    setSelectedGroupIds([...selectedGroupIds, group.id]);
    setQuery("");
  };

  const handleRemove = (id: number) => {
    setSelectedGroupIds(selectedGroupIds.filter((groupId) => groupId !== id));
  };

  // Without this, Enter here bubbles up to the surrounding <form> (SignUpForm.tsx) and submits it
  // instead of picking the typed-to match - the browser's native implicit-submit behavior for a
  // text input with no handler of its own.
  const handleQueryKeyDown = (evt: React.KeyboardEvent<HTMLInputElement>) => {
    if (evt.key !== "Enter") return;
    evt.preventDefault();
    if (filteredOptions.length > 0) handleSelect(filteredOptions[0]);
  };

  return (
    <div className="input_field" id="scope_input" ref={geoRef}>
      <label htmlFor="scope">Geographical scope</label>
      <div className="field">
        <div className="dropdown geo_scope_dropdown" onClick={() => setShowOptions(true)}>
          <div className="geo_scope_chips">
            {selectedGroups.map((group) => (
              <span className="geo_scope_chip" key={group.id}>
                {group.name}
                <button
                  type="button"
                  className="geo_scope_chip_remove"
                  onClick={(evt) => {
                    evt.stopPropagation();
                    handleRemove(group.id);
                  }}
                  aria-label={`Remove ${group.name}`}
                  data-test-id={`geo-scope-remove-${group.id}`}
                >
                  ×
                </button>
              </span>
            ))}
            <input
              type="text"
              name="scope"
              id="scope"
              value={query}
              onChange={(evt) => setQuery(evt.target.value)}
              onKeyDown={handleQueryKeyDown}
              onFocus={() => setShowOptions(true)}
              placeholder={selectedGroups.length ? "" : "Select group(s)"}
              autoComplete="off"
              data-test-id="geo-scope-input"
            />
          </div>
          <span className="arrow_down"></span>
        </div>
        {showOptions && (
          <ul className="dropdown-options" id="geography_options">
            {filteredOptions.length ? (
              filteredOptions.map((group) => (
                <li key={group.id} onClick={() => handleSelect(group)} data-test-id={`geo-scope-option-${group.id}`}>
                  {group.name}
                </li>
              ))
            ) : (
              <li className="geo_scope_no_match">No matches</li>
            )}
          </ul>
        )}
      </div>
    </div>
  );
};
